import axios, { isAxiosError } from "axios";
import { resetAuthSession } from "./authSession";
import { persistAuthTokens, readPersistedAuthTokens } from "./authStorage";
import { userIdFromAccessToken } from "./jwtUserId";
import { isPublicAppPath, normalizeAppPath } from "./publicAppRoutes";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

export const REFRESH_REQUEST_TIMEOUT_MS = 95_000;
export const MAX_REFRESH_RETRY_MS = 120_000;
const AUTH_REFRESH_LOCK = "familyhub-auth-refresh";

export type RefreshResult =
  | { ok: true; access: string; refresh: string }
  | {
      ok: false;
      fatal: boolean;
      exhausted?: boolean;
      /** Another tab signed in as a different user while refresh was in flight. */
      accountSwitch?: boolean;
    };

type TokenSink = (access: string, refresh: string) => void;
type UserIdResolver = () => number | null;

let inFlightRefresh: Promise<RefreshResult> | null = null;
let inFlightRefreshKey: string | null = null;
let tokenSink: TokenSink | null = null;
let currentUserIdResolver: UserIdResolver | null = null;
let authClearedSink: (() => void) | null = null;
let backendUnavailableSink: ((unavailable: boolean) => void) | null = null;
let waking = false;
let wakingSessionSeq = 0;
let activeWakingSession = 0;
const wakingListeners = new Set<(value: boolean) => void>();

export function registerAuthTokenSink(sink: TokenSink | null): void {
  tokenSink = sink;
}

export function registerCurrentUserIdResolver(resolver: UserIdResolver | null): void {
  currentUserIdResolver = resolver;
}

export function registerAuthClearedSink(sink: (() => void) | null): void {
  authClearedSink = sink;
}

export function registerBackendUnavailableSink(
  sink: ((unavailable: boolean) => void) | null,
): void {
  backendUnavailableSink = sink;
}

export function notifyBackendUnavailable(): void {
  backendUnavailableSink?.(true);
}

export function notifyAuthCleared(): void {
  authClearedSink?.();
}

export function invalidateInFlightRefresh(): void {
  inFlightRefresh = null;
  inFlightRefreshKey = null;
}

export function getAuthWaking(): boolean {
  return waking;
}

export function subscribeAuthWaking(listener: (value: boolean) => void): () => void {
  wakingListeners.add(listener);
  listener(waking);
  return () => {
    wakingListeners.delete(listener);
  };
}

function setAuthWaking(value: boolean): void {
  if (waking === value) return;
  waking = value;
  for (const listener of wakingListeners) {
    listener(value);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function remainingBudgetMs(started: number, now = Date.now()): number {
  return MAX_REFRESH_RETRY_MS - (now - started);
}

/** Timeout for a single refresh POST within the global retry budget. */
export function refreshAttemptTimeoutMs(startedAt: number, now = Date.now()): number {
  return Math.min(REFRESH_REQUEST_TIMEOUT_MS, remainingBudgetMs(startedAt, now));
}

function isTransientRefreshError(error: unknown): boolean {
  if (!isAxiosError(error)) {
    return true;
  }
  if (!error.response) {
    return true;
  }
  const status = error.response.status;
  if (status === 502 || status === 503 || status === 504) {
    return true;
  }
  return false;
}

function isFatalRefreshError(error: unknown): boolean {
  if (!isAxiosError(error) || !error.response) {
    return false;
  }
  const status = error.response.status;
  if (status === 401) {
    return true;
  }
  if (status === 400) {
    const data = error.response.data;
    if (data && typeof data === "object") {
      const code = (data as { code?: string }).code;
      if (code === "token_not_valid") {
        return true;
      }
    }
  }
  return false;
}

function retryDelayMs(error: unknown, attempt: number): number {
  if (isAxiosError(error) && error.response) {
    const retryAfter = error.response.headers?.["retry-after"];
    if (retryAfter) {
      const asNumber = Number(retryAfter);
      if (!Number.isNaN(asNumber) && asNumber > 0) {
        return asNumber * 1000;
      }
    }
  }
  const base = Math.min(15_000, 1_000 * 2 ** attempt);
  return base;
}

function applyRefreshedTokens(access: string, refresh: string): void {
  persistAuthTokens(access, refresh);
  tokenSink?.(access, refresh);
}

/**
 * Persist a rotation only if storage still holds the refresh token we posted.
 * Safe when auth generation changes (hydrate) or when logout/account switch races refresh.
 */
function commitRotatedTokensIfCurrent(
  postedRefreshToken: string,
  access: string,
  refresh: string,
  anchorUserId: number | null,
): RefreshResult {
  const storedRefresh = readPersistedAuthTokens().refreshToken;

  if (!storedRefresh) {
    return { ok: false, fatal: false };
  }

  if (storedRefresh !== postedRefreshToken) {
    const adopted = tryAdoptStoredTokens(anchorUserId);
    if (adopted.kind === "ok") {
      return { ok: true, access: adopted.access, refresh: adopted.refresh };
    }
    if (adopted.kind === "account-switch") {
      return handleAccountSwitchDuringRefresh();
    }
    return { ok: false, fatal: false };
  }

  applyRefreshedTokens(access, refresh);
  return { ok: true, access, refresh };
}

type AdoptStoredOutcome =
  | { kind: "ok"; access: string; refresh: string }
  | { kind: "account-switch" }
  | { kind: "missing" };

function tryAdoptStoredTokens(anchorUserId: number | null): AdoptStoredOutcome {
  const { accessToken, refreshToken } = readPersistedAuthTokens();
  if (!accessToken || !refreshToken) {
    return { kind: "missing" };
  }

  const storedUserId = userIdFromAccessToken(accessToken);
  const resolvedAnchor =
    anchorUserId ?? currentUserIdResolver?.() ?? storedUserId;

  if (
    storedUserId !== null &&
    resolvedAnchor !== null &&
    storedUserId !== resolvedAnchor
  ) {
    return { kind: "account-switch" };
  }

  tokenSink?.(accessToken, refreshToken);
  return { kind: "ok", access: accessToken, refresh: refreshToken };
}

function handleAccountSwitchDuringRefresh(): RefreshResult {
  resetAuthSession();
  return { ok: false, fatal: false, accountSwitch: true };
}

type ParsedRefreshBody =
  | { kind: "tokens"; access: string; refresh?: string }
  | { kind: "warming" }
  | { kind: "invalid" };

export function parseRefreshResponseBody(data: unknown): ParsedRefreshBody {
  if (!data || typeof data !== "object") {
    return { kind: "invalid" };
  }
  const body = data as Record<string, unknown>;
  if (body.status === "warming") {
    return { kind: "warming" };
  }
  const access = body.access;
  if (typeof access !== "string" || access.length === 0) {
    return { kind: "invalid" };
  }
  const refresh = body.refresh;
  return {
    kind: "tokens",
    access,
    refresh: typeof refresh === "string" ? refresh : undefined,
  };
}

async function postRefresh(
  refreshToken: string,
  timeoutMs: number,
): Promise<ParsedRefreshBody> {
  const response = await axios.post(
    `${API_BASE_URL}/auth/refresh/`,
    { refresh: refreshToken },
    { timeout: timeoutMs },
  );
  return parseRefreshResponseBody(response.data);
}

async function refreshWithRetries(
  refreshBeforeLock: string | null,
  userIdBeforeLock: number | null,
): Promise<RefreshResult> {
  const started = Date.now();
  let attempt = 0;
  let adoptedRotatedRefresh = false;
  const anchorUserId = userIdBeforeLock ?? currentUserIdResolver?.() ?? null;
  const wakingSession = ++wakingSessionSeq;
  activeWakingSession = wakingSession;

  try {
    while (remainingBudgetMs(started) > 0) {
      const stored = readPersistedAuthTokens();
      const { refreshToken } = stored;
      if (!refreshToken) {
        return { ok: false, fatal: false };
      }

      if (
        refreshBeforeLock &&
        refreshToken !== refreshBeforeLock &&
        stored.accessToken
      ) {
        const adopted = tryAdoptStoredTokens(anchorUserId);
        if (adopted.kind === "ok") {
          return { ok: true, access: adopted.access, refresh: adopted.refresh };
        }
        if (adopted.kind === "account-switch") {
          return handleAccountSwitchDuringRefresh();
        }
      }

      const timeoutMs = refreshAttemptTimeoutMs(started);
      if (timeoutMs <= 0) {
        return { ok: false, fatal: false, exhausted: true };
      }

      try {
        const parsed = await postRefresh(refreshToken, timeoutMs);
        if (parsed.kind === "tokens") {
          const access = parsed.access;
          const refresh = parsed.refresh ?? refreshToken;
          return commitRotatedTokensIfCurrent(
            refreshToken,
            access,
            refresh,
            anchorUserId,
          );
        }

        setAuthWaking(true);
        const delay = Math.min(15_000, 1_000 * 2 ** attempt);
        attempt += 1;
        if (remainingBudgetMs(started) - delay <= 0) {
          return { ok: false, fatal: false, exhausted: true };
        }
        await sleep(delay);
        continue;
      } catch (error) {
        if (isFatalRefreshError(error)) {
          if (
            !adoptedRotatedRefresh &&
            isAxiosError(error) &&
            error.response?.status === 401
          ) {
            const latest = readPersistedAuthTokens().refreshToken;
            if (latest && latest !== refreshToken) {
              adoptedRotatedRefresh = true;
              const adopted = tryAdoptStoredTokens(anchorUserId);
              if (adopted.kind === "ok") {
                return { ok: true, access: adopted.access, refresh: adopted.refresh };
              }
              if (adopted.kind === "account-switch") {
                return handleAccountSwitchDuringRefresh();
              }
              continue;
            }
          }
          return { ok: false, fatal: true };
        }

        if (!isTransientRefreshError(error)) {
          return { ok: false, fatal: false };
        }

        setAuthWaking(true);
        const delay = retryDelayMs(error, attempt);
        attempt += 1;
        if (remainingBudgetMs(started) - delay <= 0) {
          return { ok: false, fatal: false, exhausted: true };
        }
        await sleep(delay);
      }
    }

    return { ok: false, fatal: false, exhausted: true };
  } finally {
    if (activeWakingSession === wakingSession) {
      setAuthWaking(false);
    }
  }
}

async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request(AUTH_REFRESH_LOCK, fn);
  }
  return fn();
}

/**
 * Single-flight token refresh shared by the API 401 interceptor and auth store hydration.
 */
export function refreshTokens(): Promise<RefreshResult> {
  const { accessToken, refreshToken } = readPersistedAuthTokens();
  if (!refreshToken) {
    return Promise.resolve({ ok: false, fatal: true });
  }

  if (inFlightRefresh && inFlightRefreshKey === refreshToken) {
    return inFlightRefresh;
  }

  const refreshBeforeLock = refreshToken;
  const userIdBeforeLock = userIdFromAccessToken(accessToken);
  const keyForThisFlight = refreshBeforeLock;

  inFlightRefreshKey = keyForThisFlight;
  inFlightRefresh = withRefreshLock(() =>
    refreshWithRetries(refreshBeforeLock, userIdBeforeLock),
  ).finally(() => {
    if (inFlightRefreshKey === keyForThisFlight) {
      inFlightRefresh = null;
      inFlightRefreshKey = null;
    }
  });

  return inFlightRefresh;
}

export async function revokeRefreshToken(
  refreshToken: string | null,
  accessToken: string | null,
): Promise<void> {
  if (!refreshToken) {
    return;
  }
  try {
    await axios.post(
      `${API_BASE_URL}/auth/logout/`,
      { refresh: refreshToken },
      {
        timeout: 10_000,
        headers: accessToken
          ? { Authorization: `Bearer ${accessToken}` }
          : undefined,
      },
    );
  } catch {
    /* best effort */
  }
}

export function redirectToLoginAfterAuthFailure(): void {
  if (typeof window === "undefined") {
    return;
  }
  if (isPublicAppPath(window.location.pathname)) {
    return;
  }
  const target = "/login";
  if (normalizeAppPath(window.location.pathname) === target) {
    return;
  }
  window.location.href = target;
}
